/**
 * Where each dashboard figure drills down to.
 *
 * One place, so a card and the page it opens cannot drift apart: every link
 * lands on the page, tab and filters that show the SAME rows the figure
 * counted. The target pages read these query params on load.
 */

/** `YYYY-MM-DD` in the operator's own timezone (what the date pickers use). */
export function localDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** A period as inclusive local days (`YYYY-MM-DD`) — what the date pickers take. */
export interface DayRange {
  from: string;
  to: string;
}

/**
 * The dashboard's range (ISO, end EXCLUSIVE) as inclusive local days: a range
 * ending at midnight tomorrow covers up to today.
 */
export function dayRange(range: { from: string; to: string }): DayRange {
  const end = new Date(new Date(range.to).getTime() - 1);
  return { from: localDate(new Date(range.from)), to: localDate(end) };
}

export const dashboardLinks = {
  /** Cash captured today → Recharges & refunds, captured, today. */
  revenueToday: (now: Date) => `/refunds?status=captured&from=${localDate(now)}&to=${localDate(now)}`,
  /** Cash captured this month → same page, 1st of the month to today. */
  revenueMonth: (now: Date) =>
    `/refunds?status=captured&from=${localDate(new Date(now.getFullYear(), now.getMonth(), 1))}&to=${localDate(now)}`,
  revenueChart: '/analytics',
  /** Every call, newest first. */
  calls: '/calls',
  /** Calls connected right now. */
  activeCalls: '/calls?status=connected',
  /** One live call, with its detail drawer open. */
  liveCall: (id: string) => `/calls?status=connected&call=${encodeURIComponent(id)}`,
  /** Dropped + flagged + rejected, today — the three statuses the KPI counts. */
  failedCallsToday: '/calls?status=failed&since=today',
  /** Listeners → "Top online" tab. */
  onlineListeners: '/listeners?status=online',
  kyc: '/kyc',
  moderation: '/moderation',
  payouts: '/payouts',
  /** Every OPEN incident, whenever it happened (the KPI has no time window). */
  openIncidents: '/incidents?status=open&window=all',
  /** Active call-delivery alerts (rings not reaching phones). */
  callDelivery: '/calls/delivery',
  /** The "Recent app failures" list: any status, any time. */
  allIncidents: '/incidents?status=all&window=all',
  /** One incident, with its detail drawer open. */
  incident: (id: string) => `/incidents?status=all&window=all&incident=${encodeURIComponent(id)}`,
  // ---- Needs attention ----
  pendingListeners: '/listeners?status=pending',
  voiceVerification: '/voice-verification',
  support: '/support',
  paidNotCredited: '/refunds?status=paid_credit_pending',
  // ---- Right now ----
  busyListeners: '/listeners?status=busy',
  liveListeners: '/listeners?status=live',
  allListeners: '/listeners?status=all',
  rejectedListeners: '/listeners?status=rejected',
  ringingCalls: '/calls?status=ringing',
  liveStreams: '/live',
  // ---- Period figures: the same range, on the page that lists the rows ----
  /** Calls started in the period, optionally one outcome / the failed statuses. */
  callsInRange: (r: DayRange, filter?: { outcome?: string; status?: string }) =>
    `/calls?from=${r.from}&to=${r.to}` +
    (filter?.outcome ? `&outcome=${filter.outcome}` : '') +
    (filter?.status ? `&status=${filter.status}` : ''),
  /** Captured recharges in the period. */
  rechargesInRange: (r: DayRange) => `/refunds?status=captured&from=${r.from}&to=${r.to}`,
  users: '/users',
  coinsPerListener: '/caller-coins',
  /** Users who finished registration. */
  registeredUsers: '/users?onboarding=completed',
  /** Users who have not finished registration. */
  incompleteUsers: '/users?onboarding=incomplete',
  /** Users who stopped at one registration step. */
  incompleteAtStep: (step: number) => `/users?onboarding=incomplete&step=${step}`,
} as const;
